import React, { useEffect } from 'react';
import {
  Input,
  InputLabel,
  FormControl,
  FormHelperText,
} from '@material-ui/core';
import { InputProps } from '@material-ui/core/Input';
import { useField, useFormikContext } from 'formik';
import { QuestionCircleIcon } from '../icon/solid'
import { InputLabelProps } from '@material-ui/core/InputLabel';
import { InputBaseComponentProps } from '@material-ui/core/InputBase';
import { useId } from '../../hooks';
import { ClassApi } from '../../types/styles';
import Tooltip from '../tooltip/Tooltip';
import useStyles from './styles';
import { CustomActionCell } from '../index';

type Props = Omit<InputProps, 'classes'> & {
  label?: string;
  tooltip?: string;
  name: string;
  hideErrors?: boolean;
  dontCheckEmptyValue?: boolean;
  dontCheckAfterSubmit?: boolean;
  type?: 'text' | 'password' | 'textarea' | 'number';
  classes?: ClassApi<typeof useStyles>;
  inputLabelProps?: Omit<InputLabelProps, 'classes'>;
  getValueAfterInit?: (() => string);
  helpInfo?: string | React.ReactNode;
};

const defaultInputProps: Partial<InputBaseComponentProps> = {
  spellCheck: false,
};

const FormInput = ({
  name,
  label,
  tooltip = '',
  disabled = false,
  hideErrors = false,
  dontCheckEmptyValue = true,
  dontCheckAfterSubmit = false,
  className,
  type = 'text',
  endAdornment,
  classes: externalClasses = {},
  inputLabelProps: labelProps = {},
  inputProps = {},
  getValueAfterInit = undefined,
  helpInfo,
  ...muiInputProps
}: Props) => {
  const formContext = useFormikContext();
  const { isSubmitting } = useFormikContext();
  const classes = useStyles({ classes: externalClasses });
  const [field, meta] = useField({ name });

  // set default value if property not exist
  field.value = field.value === null ? '' : field.value;

  const inputId = useId('form-input-');
  const errorId = useId('input-error-');

  const extras: Partial<InputProps> = type === 'textarea' ? {
    multiline: true,
    rowsMax: 10,
  } : {
    type,
  };

  const checkValue = dontCheckEmptyValue || meta.value;
  const afterSubmit = dontCheckAfterSubmit && isSubmitting;
  const { setFieldValue, getFieldProps, setFieldTouched } = formContext;

  useEffect(() => {
    if (getValueAfterInit) {
      const currValue = getFieldProps(name).value;

      if (!currValue && setFieldValue) {
        const value = getValueAfterInit();
        if (value) {
          setTimeout(() => {
            setFieldValue(name as never, value, true);
            setFieldTouched(name as never, true, true);
          }, 1);
        }
      }
    }
  }, [name, setFieldValue, setFieldTouched, getValueAfterInit, getFieldProps]);

  return (
    <FormControl
      fullWidth
      disabled={disabled}
      error={Boolean(!isSubmitting) && Boolean(!afterSubmit && checkValue && meta.touched && meta.error)}
      className={className}
      classes={{
        root: classes.root,
      }}
    >
      {label && (
        <Tooltip title={tooltip}>
          <InputLabel
            htmlFor={inputId}
            classes={{
              root: classes.labelRoot,
              focused: classes.focused,
            }}
            {...labelProps}
          >
            <>
              {label}
              {tooltip && <CustomActionCell
                title={<QuestionCircleIcon className={`${classes.iconTooltip}`} />}
                classNameButtonWrapper={classes.commentButtonWrapper}
                onMouse
              >
                <p className={classes.comment}>
                  {tooltip}
                </p>
              </CustomActionCell>}
            </>
          </InputLabel>
        </Tooltip>
      )}
      <Input
        id={inputId}
        disabled={disabled}
        aria-describedby={errorId}
        endAdornment={endAdornment}
        {...field}
        {...extras}
        {...muiInputProps}
        inputProps={{
          ...defaultInputProps,
          ...inputProps,
        }}
        classes={{
          input: classes.input,
          underline: classes.inputUnderline,
          focused: classes.focused,
          disabled: classes.disabled,
        }}
      />
      {!afterSubmit && checkValue && meta.touched && meta.error && !hideErrors && (
        <FormHelperText id={errorId}>{meta.error}</FormHelperText>
      )}
      {!(!afterSubmit && checkValue && meta.touched && meta.error && !hideErrors) && helpInfo && (
        <FormHelperText><QuestionCircleIcon className={classes.helpIcon} />{helpInfo}</FormHelperText>
      )}
    </FormControl>
  );
};

export default FormInput;
